import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Music2, Sparkles } from "lucide-react";
import { useAppData } from "@/context/AppData";

const NAV = [
  ["/occasions", "Occasions"],
  ["/how-it-works", "How It Works"],
  ["/examples", "Examples"],
  ["/pricing", "Pricing"],
  ["/track", "Track Order"],
];

export const Brand = () => {
  const { config } = useAppData() || {};
  return (
    <Link to="/" data-testid="nav-brand-logo" className="flex items-center gap-2">
      {config?.logo_url ? (
        <img src={config.logo_url} alt={config.brand_name} className="h-8 w-auto" />
      ) : (
        <>
          <span className="grid h-8 w-8 place-items-center rounded-full bg-[var(--plum)] text-[var(--gold)]">
            <Music2 className="h-4 w-4" aria-hidden />
          </span>
          <span className="font-display text-xl tracking-tight">{config?.brand_name || "StoryMelody"}</span>
        </>
      )}
    </Link>
  );
};

export const DevBanner = () => (
  <div data-testid="launch-banner" className="flex items-center justify-center gap-2 bg-[var(--plum)] px-4 py-2 text-center text-xs text-[var(--ivory)] sm:text-sm">
    <Sparkles className="h-3.5 w-3.5 shrink-0 text-[var(--gold)]" aria-hidden="true" />
    <span>Turn your favorite memories into a custom, studio-quality song today!</span>
  </div>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 border-b border-[var(--parchment)]/80 bg-[var(--ivory)]/90 backdrop-blur-xl">
      <DevBanner />
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <Brand />
        <nav className="hidden items-center gap-7 lg:flex" aria-label="Main">
          {NAV.map(([to, label]) => (
            <NavLink key={to} to={to} data-testid={`nav-${to.slice(1)}`}
              className={({ isActive }) => `text-sm transition-colors hover:text-[var(--rose)] ${isActive ? "text-[var(--rose)]" : "text-[var(--plum-2)]"}`}>
              {label}
            </NavLink>
          ))}
          <Link to="/create" data-testid="nav-create-button" className="btn-gold !px-5 !py-2.5 text-sm">Create Their Song</Link>
        </nav>
        <button className="lg:hidden" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open}
          data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)}>
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <nav className="flex flex-col gap-1 border-t border-[var(--parchment)] px-5 pb-5 lg:hidden" aria-label="Mobile">
          {NAV.map(([to, label]) => (
            <Link key={to} to={to} onClick={() => setOpen(false)} className="py-3 text-[var(--plum-2)]" data-testid={`mobile-nav-${to.slice(1)}`}>{label}</Link>
          ))}
          <Link to="/create" onClick={() => setOpen(false)} className="btn-gold mt-2">Create Their Song</Link>
        </nav>
      )}
    </header>
  );
};

export const Footer = () => {
  const { config } = useAppData() || {};
  const links = [["/faq", "FAQ"], ["/contact", "Contact"], ["/privacy", "Privacy Policy"], ["/terms", "Terms"], ["/refunds", "Refund & Revision Policy"]];
  return (
    <footer className="mt-24 bg-[var(--plum)] text-[var(--ivory)]">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 sm:px-8 md:grid-cols-3">
        <div>
          <p className="font-display text-2xl">{config?.brand_name || "StoryMelody"}</p>
          <p className="mt-3 max-w-xs text-sm text-[var(--ivory)]/70">Personalized songs made from your memories. AI-generated, reviewed by a person during our pilot.</p>
        </div>
        <nav className="flex flex-col gap-2 text-sm" aria-label="Footer">
          {links.map(([to, l]) => <Link key={to} to={to} className="text-[var(--ivory)]/80 hover:text-[var(--gold)]" data-testid={`footer-${to.slice(1)}`}>{l}</Link>)}
        </nav>
        <div className="text-sm text-[var(--ivory)]/70">
          <p>Prices in {config?.currency || "USD"}. Website in English.</p>
          {config?.support_email && <p className="mt-2">Support: <a className="underline" href={`mailto:${config.support_email}`}>{config.support_email}</a></p>}
          <Link to="/admin" className="mt-4 inline-block text-xs text-[var(--ivory)]/40 hover:text-[var(--ivory)]/80" data-testid="footer-admin">Team login</Link>
        </div>
      </div>
    </footer>
  );
};

export const Page = ({ eyebrow, title, intro, children, testid }) => (
  <main className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20" data-testid={testid}>
    {eyebrow && <p className="eyebrow rise">{eyebrow}</p>}
    {title && <h1 className="font-display rise mt-3 text-4xl leading-tight sm:text-5xl lg:text-6xl">{title}</h1>}
    {intro && <p className="rise mt-5 max-w-2xl text-base text-[var(--plum-2)] md:text-lg">{intro}</p>}
    <div className="mt-12">{children}</div>
  </main>
);
